import { makeAutoObservable } from 'mobx'
import type { BrandPlanScheduleContent } from 'types'
import { NavigationCountsResponse } from 'hooks/queries/calendar/useCalendarNavigationCounts'

export class ContentGenerationStore {
  calendarNavigationCounts: NavigationCountsResponse | null = null
  calendarOnPrev: (() => void) | null = null
  calendarOnNext: (() => void) | null = null

  generatingScheduleId: number | null = null
  brandPlanId: number | null = null
  isGenerationDone = false
  generatingContents: BrandPlanScheduleContent[] = []
  completedCount = 0
  totalCount = 0
  failedCount = 0
  successCount = 0
  numberOfNewPosts = 0
  regeneratingContentIds: number[] = []
  hasScheduleData = false
  showQuestionnaire = false
  isQuestionnaireMinimized = false
  shouldYieldToFooter = false
  latestGenerationCompletedAt: Date | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isActive() {
    return this.generatingScheduleId !== null && !this.isGenerationDone
  }

  get isVisible() {
    return this.isActive || this.isGenerationDone
  }

  get hasOtherScheduleContents() {
    return this.generatingContents.some(
      (c) => c.brand_plan_schedule_id != null && c.brand_plan_schedule_id !== this.generatingScheduleId,
    )
  }

  get startedAt(): number | null {
    if (this.generatingContents.length === 0) return null
    let earliest = Infinity
    for (const content of this.generatingContents) {
      const ts = new Date(content.created_at).getTime()
      if (ts < earliest) earliest = ts
    }
    return earliest === Infinity ? null : earliest
  }

  setGeneratingSchedule(scheduleId: number, brandPlanId: number) {
    if (this.generatingScheduleId === scheduleId && !this.isGenerationDone) return
    const otherScheduleContents = this.generatingContents.filter(
      (c) => c.brand_plan_schedule_id != null && c.brand_plan_schedule_id !== this.generatingScheduleId,
    )
    this.generatingScheduleId = scheduleId
    this.brandPlanId = brandPlanId
    this.isGenerationDone = false
    this.generatingContents = otherScheduleContents
    this.totalCount = otherScheduleContents.length
    this.completedCount = 0
    this.failedCount = 0
    this.successCount = 0
    this.hasScheduleData = false
    this.regeneratingContentIds = []
    this.latestGenerationCompletedAt = null
  }

  /**
   * Pre-seeds generating state during regeneration so calendar temp events appear immediately
   * (before useContentGenerationWatcher polls schedule details). Replaced by updateProgress
   * when real schedule contents arrive.
   */
  initRegeneration(scheduleId: number, brandPlanId: number, contents: BrandPlanScheduleContent[]) {
    this.generatingScheduleId = scheduleId
    this.brandPlanId = brandPlanId
    this.isGenerationDone = false
    this.regeneratingContentIds = contents.map((c) => c.id)
    this.generatingContents = contents
    this.totalCount = contents.length
    this.completedCount = 0
    this.failedCount = 0
    this.successCount = 0
    this.hasScheduleData = false
    this.latestGenerationCompletedAt = null
  }

  updateProgress(contents: BrandPlanScheduleContent[], completed: number, failed: number, successful: number) {
    const incomingIds = new Set(contents.map((c) => c.id))
    const otherContents = this.generatingContents.filter((c) => !incomingIds.has(c.id))
    this.generatingContents = [...otherContents, ...contents]
    this.totalCount = this.generatingContents.length
    this.completedCount = completed
    this.failedCount = failed
    this.successCount = successful
    this.hasScheduleData = true
  }

  setNumberOfNewPosts(count: number) {
    this.numberOfNewPosts = count
  }

  setShowQuestionnaire(show: boolean) {
    this.showQuestionnaire = show
  }

  setQuestionnaireMinimized(minimized: boolean) {
    this.isQuestionnaireMinimized = minimized
  }

  setShouldYieldToFooter(shouldYield: boolean) {
    this.shouldYieldToFooter = shouldYield
  }

  setCalendarNavigation(
    counts: NavigationCountsResponse | null,
    onPrev: (() => void) | null,
    onNext: (() => void) | null,
  ) {
    this.calendarNavigationCounts = counts
    this.calendarOnPrev = onPrev
    this.calendarOnNext = onNext
  }

  clearCalendarNavigation() {
    this.calendarNavigationCounts = null
    this.calendarOnPrev = null
    this.calendarOnNext = null
  }

  markDone() {
    this.isGenerationDone = true
    this.latestGenerationCompletedAt = new Date()
  }

  reset() {
    this.generatingScheduleId = null
    this.brandPlanId = null
    this.isGenerationDone = false
    this.generatingContents = []
    this.completedCount = 0
    this.totalCount = 0
    this.failedCount = 0
    this.successCount = 0
    this.numberOfNewPosts = 0
    this.regeneratingContentIds = []
    this.hasScheduleData = false
    this.showQuestionnaire = false
    this.isQuestionnaireMinimized = false
    this.shouldYieldToFooter = false
    this.latestGenerationCompletedAt = null
  }
}
